import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/cms';
import { getLocalized } from '../lib/utils';
import { servicesData } from '../data';

const Services = () => {
  const [services, setServices] = useState(servicesData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollection('services').then(data => {
      if (data && data.length > 0) setServices(data);
      setLoading(false);
    });
  }, []);

  // Show all services except forandringsprocesser
  const mainServices = services
    .filter(s => getLocalized(s.path) !== 'forandringsprocesser')
    .sort((a, b) => {
      const order = ['interim-assistance', 'cfo-sparring', 'teamudvikling-workshops', 'podcast', 'netvaerk'];
      const pathA = getLocalized(a.path);
      const pathB = getLocalized(b.path);
      return order.indexOf(pathA) - order.indexOf(pathB);
    });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-stone-400 animate-pulse font-serif text-xl">Henter ydelser...</div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Helmet>
        <title>Ydelser - piameyer.dk</title>
        <meta name="description" content="Jeg hjælper virksomheder med at skabe kvalitet og trivsel i økonomifunktionen med udgangspunkt i mennesker, processer og ledelse." />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-8 bg-[#f7f4ed]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl lg:text-6xl font-serif text-stone-900 mb-4">Hvordan kan jeg hjælpe dig?</h1>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-0">
        <div className="w-full">
          <div className="space-y-0">
            {mainServices.map((service, index) => {
              const title = getLocalized(service.title);
              const summary = getLocalized(service.summary);
              const benefits = getLocalized(service.benefits);
              const path = getLocalized(service.path);
              const image = service.image?.[0];
              const priceInfo = getLocalized(service.price_info);
              const cleanPriceInfo = priceInfo
                ? priceInfo.replace(/^(Interim assistance|CFO Sparring|Teamudvikling|Podcast|Netværk):\s*/i, '')
                : '';

              const benefitList = String(benefits || '')
                .replace(/\\n/g, '\n')
                .split('\n')
                .map(b => b.trim())
                .filter(Boolean);

              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={service.id}
                  id={path}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="flex flex-col lg:flex-row items-stretch min-h-[600px] border-b border-stone-100 last:border-0"
                >
                  {/* Image Column */}
                  <div className={`w-full lg:w-1/2 relative min-h-[400px] lg:min-h-0 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <img
                      src={image}
                      alt={title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>

                  {/* Content Column */}
                  <div className={`w-full lg:w-1/2 flex items-center ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className={`py-16 lg:py-24 px-6 sm:px-12 lg:px-20 xl:px-32 max-w-3xl ${!isEven ? 'ml-auto' : ''}`}>
                      <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-6">{title}</h2>
                      <p className="text-stone-600 text-lg leading-relaxed mb-8">{summary}</p>
                      
                      {benefitList.length > 0 && (
                        <div className="mb-8 space-y-3">
                          <p className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-4">Typiske behov er:</p>
                          {benefitList.map((benefit, i) => (
                            <div key={i} className="flex items-start gap-3 text-stone-700">
                              <Check size={18} className="text-black shrink-0 mt-1" />
                              <span className="leading-snug">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {cleanPriceInfo && (
                        <div className="mb-10 p-5 bg-stone-50 rounded-xl border border-stone-200/60 text-stone-700 text-base leading-relaxed">
                          <p className="text-xs font-bold text-[#204f3d] uppercase tracking-widest mb-1">Pris & Info</p>
                          <p className="text-stone-700">{cleanPriceInfo}</p>
                        </div>
                      )}
                      
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center px-8 py-4 border border-[#204f3d] text-[#204f3d] rounded-full font-medium hover:bg-[#204f3d] hover:text-white transition-all"
                      >
                        Kontakt mig om denne ydelse
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Price Info */}
      <section className="py-20 bg-[#f7f4ed] border-t border-stone-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-serif text-stone-900 mb-6">Priser & Forløb</h2>
            <div className="bg-white p-8 md:p-12 rounded-2xl border border-stone-200/60 shadow-sm">
              <p className="text-xl text-stone-700 leading-relaxed">
                Pris aftales nærmere, jeg tilbyder både timepris samt skræddersyede forløb.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-stone-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 border border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-96 h-96 border border-white rounded-full" />
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl font-serif text-white mb-6">Nysgerrig på at høre mere?</h2>
          <p className="text-stone-400 text-xl mb-12 max-w-2xl mx-auto">
            Lad os tage en uforpligtende snak om, hvordan jeg kan hjælpe jer med at skabe en økonomifunktion, der fungerer både fagligt og menneskeligt.
          </p>
          <Link
            to="/kontakt"
            className="inline-block px-10 py-5 bg-white text-stone-900 rounded-full font-bold text-lg hover:bg-stone-100 transition-all shadow-xl"
          >
            Book et kort møde
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
